import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight, GraduationCap, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSettings } from "@/context/SettingsContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Courses() {
  const { settings } = useSettings();
  const c = settings?.courses || {};
  const items = c.items || [];
  const otherPrograms = c.otherPrograms || [];

  return (
    <div data-testid="courses-page">
      <section className="py-20 lg:py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p {...fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{c.eyebrow}</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight max-w-4xl">
            {c.heading}
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="text-slate-600 text-lg mt-5 max-w-2xl">{c.intro}</motion.p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {items.map((course, i) => (
              <motion.article
                key={course.slug || i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.06 }}
                data-testid={`course-card-${course.slug || i}`}
                style={i % 2 !== 0 ? { backgroundColor: "var(--brand-dark)" } : {}}
                className={`group rounded-3xl p-8 sm:p-10 border transition-all duration-300 hover:-translate-y-1 ${
                  i % 2 === 0
                    ? "bg-white border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_50px_rgba(0,0,0,0.08)]"
                    : "text-white border-transparent shadow-[0_20px_40px_rgba(15,23,42,0.15)] hover:shadow-[0_30px_60px_rgba(15,23,42,0.25)]"
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Badge
                    variant="secondary"
                    className={`rounded-full px-3 py-1 text-[11px] tracking-wider uppercase font-semibold ${
                      i % 2 === 0 ? "bg-rose-50 text-rose-600 hover:bg-rose-50" : "bg-white/10 text-rose-300 hover:bg-white/10"
                    }`}
                  >
                    {course.tag}
                  </Badge>
                  <div
                    className="w-11 h-11 rounded-xl grid place-items-center"
                    style={
                      i % 2 === 0
                        ? { backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }
                        : { backgroundColor: "rgba(255,255,255,0.1)", color: "#fff" }
                    }
                  >
                    <GraduationCap className="w-5 h-5" />
                  </div>
                </div>

                <h2 className={`font-heading text-2xl sm:text-3xl leading-tight ${i % 2 === 0 ? "text-slate-900" : "text-white"}`}>{course.title}</h2>
                <div className={`flex items-center gap-2 mt-3 text-sm ${i % 2 === 0 ? "text-slate-500" : "text-slate-300"}`}>
                  <Clock className="w-4 h-4" /> Duration: {course.duration}
                </div>
                <p className={`mt-5 leading-relaxed ${i % 2 === 0 ? "text-slate-600" : "text-slate-300"}`}>{course.desc}</p>

                {course.specializations && course.specializations.length > 0 && (
                  <div className="mt-6">
                    <p className={`text-xs font-bold uppercase tracking-[0.2em] mb-3 ${i % 2 === 0 ? "brand-secondary-text" : "text-rose-300"}`}>Specializations</p>
                    <ul className="grid sm:grid-cols-2 gap-2">
                      {course.specializations.map((s) => (
                        <li key={s} className={`flex items-start gap-2 text-sm ${i % 2 === 0 ? "text-slate-700" : "text-slate-200"}`}>
                          <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${i % 2 === 0 ? "text-emerald-500" : "text-emerald-400"}`} />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-8">
                  <Link to="/contact#inquiry" data-testid={`apply-${course.slug || i}`}>
                    <Button
                      style={i % 2 === 0 ? { backgroundColor: "var(--brand-primary)" } : { backgroundColor: "var(--brand-secondary)" }}
                      className="rounded-full h-11 px-5 text-white hover:brightness-110 transition-all hover:-translate-y-0.5"
                    >
                      Apply for this program <ArrowUpRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          {otherPrograms.length > 0 && (
            <motion.div {...fadeUp} className="mt-20">
              <div className="max-w-2xl mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Additional Programs</p>
                <h2 className="font-heading text-3xl sm:text-4xl text-slate-900 leading-tight">Other Programs</h2>
                <p className="text-slate-600 mt-3">Complementary programs from the Ambigara Group of Institutions.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {otherPrograms.map((p) => (
                  <div
                    key={p}
                    data-testid={`other-program-${p.toLowerCase()}`}
                    className="bg-white rounded-2xl p-6 border border-slate-100 flex items-center justify-between hover:shadow-md transition-all hover:-translate-y-0.5"
                  >
                    <span className="font-heading text-xl text-slate-900">{p}</span>
                    <ArrowUpRight className="w-5 h-5 text-slate-400" />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
